import type { BuildLookupRow, Catalog, ExpectedDebitsTable, GroupTotals, GroupWithMembers, MemberTotals, MemberWithRatings } from "../types";
import { ageAsOf } from "./age";
import { buildPoints } from "./build";
import { memberDebitTotals } from "./debits";
import { expectedAndBase, relationClassOf } from "./expected";

export function adjustedRmm(rmm: number, multiplier: number): number {
  return rmm * multiplier;
}

export function memberTotalsFor(member: MemberWithRatings, effectiveDate: string, expectedTable: ExpectedDebitsTable, buildTable: readonly BuildLookupRow[]): MemberTotals {
  const age = ageAsOf(member.dateOfBirth, effectiveDate);
  const build = buildPoints(member, effectiveDate, buildTable);
  const debits = memberDebitTotals(member, build);
  const expected = expectedAndBase(expectedTable, relationClassOf(member.relation), age, member.gender);
  return {
    memberId: member.id,
    age,
    build,
    rxDebits: debits.rxDebits,
    conditionDebits: debits.conditionDebits,
    totalDebits: debits.totalDebits,
    uwDollars: debits.uwDollars,
    expectedDebits: expected.expected,
    baseDebits: expected.base,
  };
}

/**
 * Group totals and RMM (product plan 4.5 to 4.7). Counts and sums include only
 * non-excluded members; Dependent Adults count with Spouses; UW Dollars total
 * separately and never touch RMM. The catalog is accepted so callers can pass the
 * same bundle they used to reconcile synthetic rows.
 */
export function groupTotals(group: GroupWithMembers, _catalog: Catalog, expectedTable: ExpectedDebitsTable, buildTable: readonly BuildLookupRow[]): GroupTotals {
  const perMember: Record<string, MemberTotals> = {};
  let employees = 0;
  let spouses = 0;
  let children = 0;
  let membersExcluded = 0;
  let totalDebits = 0;
  let uwDollars = 0;
  let numerator = 0;
  let denominator = 0;

  for (const member of group.members) {
    const totals = memberTotalsFor(member, group.effectiveDate, expectedTable, buildTable);
    perMember[member.id] = totals;
    if (member.isExcluded) {
      membersExcluded += 1;
      continue;
    }
    if (member.relation === "Employee") employees += 1;
    else if (member.relation === "Child") children += 1;
    else spouses += 1;
    totalDebits += totals.totalDebits;
    uwDollars += totals.uwDollars;
    numerator += totals.totalDebits + totals.baseDebits;
    denominator += totals.expectedDebits + totals.baseDebits;
  }

  const rmm = denominator === 0 ? 0 : numerator / denominator;
  return {
    employees,
    spouses,
    children,
    totalMembers: employees + spouses + children,
    membersExcluded,
    totalDebits,
    rmm,
    adjustedRmm: adjustedRmm(rmm, group.uwOverrideMultiplier),
    uwDollars,
    perMember,
  };
}
